import React from 'react';
import { getButtonClassName } from './buttonClasses';

function MaterialIcon({ children, position }) {
  if (!children) {
    return null;
  }

  return (
    <span className={`material-symbols-outlined button-icon button-icon-${position}`} aria-hidden="true">
      {children}
    </span>
  );
}

function BusySpinner({ active }) {
  if (!active) {
    return null;
  }

  return <span className="button-busy-spinner" aria-hidden="true" />;
}

function isPromiseLike(value) {
  return Boolean(value && typeof value.then === 'function');
}

export const Button = React.forwardRef(function Button({
  busy = false,
  children,
  className = '',
  disabled,
  disableWhileBusy = true,
  iconEnd,
  iconStart,
  type = 'button',
  variant = 'primary',
  ...props
}, ref) {
  const [clickBusy, setClickBusy] = React.useState(false);
  const pendingClickCountRef = React.useRef(0);
  const mountedRef = React.useRef(true);
  const displayBusy = Boolean(busy || clickBusy);
  const isDisabled = Boolean(disabled || (displayBusy && disableWhileBusy));

  React.useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  function finishAsyncClick() {
    pendingClickCountRef.current = Math.max(0, pendingClickCountRef.current - 1);
    if (mountedRef.current && pendingClickCountRef.current === 0) {
      setClickBusy(false);
    }
  }

  function handleClick(event) {
    // React has not necessarily rendered the disabled state between two fast
    // input events. Keep the single-flight guard in a ref so an async command
    // cannot be submitted twice during that gap.
    if (disableWhileBusy && pendingClickCountRef.current > 0) {
      event.preventDefault();
      return undefined;
    }
    const result = props.onClick?.(event);
    if (isPromiseLike(result)) {
      pendingClickCountRef.current += 1;
      setClickBusy(true);
      Promise.resolve(result).then(finishAsyncClick, finishAsyncClick);
    }
    return result;
  }

  return (
    <button
      {...props}
      aria-busy={displayBusy ? 'true' : props['aria-busy']}
      className={getButtonClassName({ className, variant })}
      disabled={isDisabled}
      onClick={props.onClick ? handleClick : undefined}
      ref={ref}
      type={type}
    >
      <BusySpinner active={displayBusy} />
      <MaterialIcon position="start">{iconStart}</MaterialIcon>
      {children}
      <MaterialIcon position="end">{iconEnd}</MaterialIcon>
    </button>
  );
});

export const ButtonLink = React.forwardRef(function ButtonLink({
  children,
  className = '',
  iconEnd,
  iconStart,
  variant = 'primary',
  ...props
}, ref) {
  return (
    <a {...props} className={getButtonClassName({ className, variant })} ref={ref}>
      <MaterialIcon position="start">{iconStart}</MaterialIcon>
      {children}
      <MaterialIcon position="end">{iconEnd}</MaterialIcon>
    </a>
  );
});
